import { useMemo } from 'react'
import { VocabularyBrain } from '@/components/ui/art/VocabularyBrain'
import { useVocabularyStats } from '@/hooks/useVocabularyStats'
import { useBrainSettings } from '@/hooks/useBrainSettings'
import { DEFAULT_POS, type PosPalette, type StrengthWeight } from '@/components/ui/art/engines/brain'
import type { PosCounts, VocabularyStats } from '@/types/vocabulary'

// Engine field cap (D8). Beyond this the brain is a representative sample;
// the header still shows the true backend total.
const NODE_CAP = 1500
// Calm minimal field shown when the learner has no known words yet.
const EMPTY_FIELD = 320
const THIN_SPACE = ' '

// status → strength values (D2). Counts come from the backend histogram.
const STATUS_STRENGTH: { value: number; key: keyof PosCounts }[] = [
  { value: 0.3, key: 'new' },
  { value: 0.55, key: 'learning' },
  { value: 0.8, key: 'review' },
  { value: 1.0, key: 'mastered' },
]

// Endpoint FULL POS names → engine palette keys (D3 bridge). "other" is skipped
// from the coloured palette (its count still counts toward the header total).
const POS_KEY_MAP: Record<string, string> = {
  noun: 'noun',
  verb: 'verb',
  adjective: 'adj',
  adverb: 'adv',
  pronoun: 'pron',
  preposition: 'prep',
  conjunction: 'conj',
  determiner: 'det',
}

function strengthsFromCounts(counts: PosCounts): StrengthWeight[] {
  return STATUS_STRENGTH.map(({ value, key }) => ({ value, weight: counts[key] }))
}

function buildPalette(stats: VocabularyStats): PosPalette {
  const palette: PosPalette = {}
  for (const k in DEFAULT_POS) palette[k] = { ...DEFAULT_POS[k] }

  const total = stats.total || 1
  for (const stat of stats.by_pos) {
    const key = POS_KEY_MAP[stat.pos]
    if (!key || !palette[key]) continue
    const sum = stat.counts.new + stat.counts.learning + stat.counts.review + stat.counts.mastered
    palette[key] = {
      ...palette[key],
      share: sum / total,
      strengths: strengthsFromCounts(stat.counts),
    }
  }
  return palette
}

function groupThousands(n: number): string {
  return n.toLocaleString('en-US').replace(/,/g, THIN_SPACE)
}

interface VocabularyBrainPanelProps {
  languageId: number
}

/** The signed-in learner's own brain for one language. */
export function VocabularyBrainPanel({ languageId }: VocabularyBrainPanelProps) {
  const { stats, loading, error } = useVocabularyStats(languageId)
  return <VocabularyBrainView stats={stats} loading={loading} error={error} />
}

interface VocabularyBrainViewProps {
  stats: VocabularyStats | null
  loading: boolean
  error: string | null
  /** Whose vocabulary this is; an admin reads someone else's. */
  title?: string
}

export function VocabularyBrainView({ stats, loading, error, title = 'Your vocabulary' }: VocabularyBrainViewProps) {
  const brainSettings = useBrainSettings()

  const palette = useMemo(() => (stats ? buildPalette(stats) : DEFAULT_POS), [stats])

  if (loading || (stats && !brainSettings)) {
    return (
      <section aria-label={`${title} as a brain`} className="space-y-4">
        <PanelHead title={title} countLabel="…" />
        <div
          data-testid="brain-panel-skeleton"
          className="h-[clamp(380px,50vw,560px)] rounded-md bg-surface-bg animate-pulse"
        />
      </section>
    )
  }

  if (error || !stats) {
    return (
      <section aria-label={`${title} as a brain`} className="space-y-4">
        <PanelHead title={title} countLabel="—" />
        <p role="alert" className="font-mono text-xs uppercase tracking-widest text-muted">
          {title} could not be loaded.
        </p>
      </section>
    )
  }

  const total = stats.total
  const count = total === 0 ? EMPTY_FIELD : Math.min(total, NODE_CAP)

  return (
    <section aria-label={`${title} as a brain`} className="space-y-5">
      <PanelHead title={title} countLabel={groupThousands(total)} />

      <div className="relative h-[clamp(380px,50vw,560px)] overflow-hidden rounded-md">
        <VocabularyBrain
          posPalette={total === 0 ? DEFAULT_POS : palette}
          count={count}
          clusterPull={brainSettings?.cluster_pull}
          linkDistance={brainSettings?.link_distance}
          className="absolute inset-0 h-full w-full"
        />
        <span className="pointer-events-none absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.28em] text-muted opacity-70">
          Drag to rotate
        </span>
      </div>

      <PosLegend palette={palette} />
    </section>
  )
}

function PanelHead({ title, countLabel }: { title: string; countLabel: string }) {
  return (
    <div className="flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1">
      <span className="font-mono text-xs uppercase tracking-[0.32em] text-accent">
        {title}
      </span>
      <span className="font-mono text-xs uppercase tracking-[0.22em] text-muted">
        <strong className="font-medium text-heading">{countLabel}</strong> words known
      </span>
    </div>
  )
}

function PosLegend({ palette }: { palette: PosPalette }) {
  return (
    <div
      data-testid="brain-legend"
      className="flex flex-wrap gap-x-6 gap-y-2 border-t border-line-soft pt-4"
    >
      {Object.keys(palette).map((key) => (
        <span
          key={key}
          data-legend-chip
          className="inline-flex items-center gap-2.5 font-serif text-sm text-body"
        >
          <span
            aria-hidden="true"
            className="inline-block h-2.5 w-2.5 rounded-sm"
            style={{ backgroundColor: palette[key].color }}
          />
          {palette[key].label}
        </span>
      ))}
    </div>
  )
}
